import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import OrgMultiSelect from './OrgMultiSelect'

const PERSONAL_CATEGORY_OPTIONS = [
    { value: '', label: '선택 안 함' },
    { value: 'MEETING', label: '회의' },
    { value: 'CLIENT_MEETING', label: '미팅' },
    { value: 'TRIP', label: '출장' },
    { value: 'LEAVE', label: '휴가' },
    { value: 'TRAINING', label: '교육' },
    { value: 'OTHER', label: '기타' },
]

const RRULE_PRESETS = [
    { key: 'none', value: '', label: '반복 없음' },
    { key: 'daily', value: 'FREQ=DAILY;INTERVAL=1', label: '매일' },
    { key: 'weekdays', value: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR', label: '매주 평일' },
    { key: 'weekly', value: 'FREQ=WEEKLY;INTERVAL=1', label: '매주 (같은 요일)' },
]

function unwrap(res) {
    const d = res?.data
    if (d && d.data !== undefined) return d.data
    return d
}

function toLocalInput(d) {
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function parseIso(s) {
    if (!s) return null
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? null : d
}

function validateEventStartEndOrder(startLocal, endLocal) {
    if (!startLocal || !endLocal) {
        return { ok: false, message: '시작·종료 시각을 입력해 주세요.' }
    }
    const s = new Date(startLocal)
    const e = new Date(endLocal)
    if (!Number.isFinite(s.getTime()) || !Number.isFinite(e.getTime())) {
        return { ok: false, message: '시작·종료 시각이 올바르지 않습니다.' }
    }
    if (!(e > s)) {
        return { ok: false, message: '시작은 종료보다 이전이어야 하고, 종료는 시작보다 이후여야 합니다.' }
    }
    return { ok: true }
}

/**
 * 일정 추가/수정 모달(일정관리·대시보드 공용). 부모는 ref로 open* 호출.
 */
const CalendarEventEditor = forwardRef(function CalendarEventEditor(_props, ref) {
    const { user } = useAuth()
    const { show } = useToast()
    const qc = useQueryClient()

    const [modal, setModal] = useState(null)
    const [editDetail, setEditDetail] = useState(null)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [startLocal, setStartLocal] = useState('')
    const [endLocal, setEndLocal] = useState('')
    const [allDay, setAllDay] = useState(false)
    const [remindersEnabled, setRemindersEnabled] = useState(true)
    const [rem10, setRem10] = useState(false)
    const [rem60, setRem60] = useState(true)
    const [rem1440, setRem1440] = useState(false)
    const [orgTargetConfig, setOrgTargetConfig] = useState([])
    const [orgPickerIds, setOrgPickerIds] = useState([])
    const [explicitUserIds, setExplicitUserIds] = useState([])
    const [candidates, setCandidates] = useState([])
    const [rrule, setRrule] = useState('')
    const [personalCategory, setPersonalCategory] = useState('')
    const [busyShareOrgId, setBusyShareOrgId] = useState(null)

    const syncOrgTargetsFromPicker = useCallback((ids) => {
        setOrgPickerIds(ids)
        setOrgTargetConfig((prev) => {
            const next = ids.map((id) => {
                const n = Number(id)
                const found = prev.find((x) => x.orgId === n)
                return found || { orgId: n, includeDescendants: true }
            })
            return next
        })
    }, [])

    const orgOptionsQuery = useQuery({
        queryKey: ['orgs-options', 'mine'],
        queryFn: async () => {
            const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
            const data = unwrap(res)
            return Array.isArray(data) ? data : []
        },
    })

    const orgNameById = useMemo(() => {
        const m = new Map()
        for (const o of orgOptionsQuery.data || []) {
            const id = Number(o.id)
            if (id) m.set(id, o.name || o.orgName || `조직 ${id}`)
        }
        return m
    }, [orgOptionsQuery.data])

    useEffect(() => {
        if ((modal !== 'org' && modal !== 'edit-org') || !orgTargetConfig.length) {
            setCandidates([])
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const body = {
                    orgTargets: orgTargetConfig.map((r) => ({
                        orgId: r.orgId,
                        includeDescendants: !!r.includeDescendants,
                    })),
                }
                const res = await api.post('/calendar/candidate-users', body)
                const data = unwrap(res)
                if (!cancelled) setCandidates(Array.isArray(data) ? data : [])
            } catch {
                if (!cancelled) setCandidates([])
            }
        })()
        return () => { cancelled = true }
    }, [modal, orgTargetConfig])

    const reminderOffsetsMinutes = useMemo(() => {
        if (!remindersEnabled) return []
        const o = []
        if (rem10) o.push(10)
        if (rem60) o.push(60)
        if (rem1440) o.push(1440)
        return o
    }, [remindersEnabled, rem10, rem60, rem1440])

    const startEndOrder = useMemo(
        () => validateEventStartEndOrder(startLocal, endLocal),
        [startLocal, endLocal],
    )

    function resetPersonalCreateDefaults() {
        setTitle('')
        setDescription('')
        setRrule('')
        setPersonalCategory('')
        setBusyShareOrgId(null)
        setAllDay(false)
        setRemindersEnabled(true)
        setRem10(false)
        setRem60(true)
        setRem1440(false)
    }

    function openPersonalAt(startDate, endDate) {
        setModal('personal')
        setEditDetail(null)
        resetPersonalCreateDefaults()
        let d = startDate instanceof Date ? new Date(startDate.getTime()) : new Date()
        if (Number.isNaN(d.getTime())) {
            d = new Date()
            d.setMinutes(0, 0, 0)
        } else if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0) {
            d.setHours(9, 0, 0, 0)
        } else {
            d.setMinutes(0, 0, 0)
        }
        let e = endDate instanceof Date ? new Date(endDate.getTime()) : null
        if (!e || Number.isNaN(e.getTime()) || !(e > d)) {
            e = new Date(d.getTime() + 60 * 60 * 1000)
        }
        setStartLocal(toLocalInput(d))
        setEndLocal(toLocalInput(e))
    }

    function openOrg() {
        setModal('org')
        setEditDetail(null)
        setTitle('')
        setDescription('')
        setRrule('')
        setPersonalCategory('')
        setBusyShareOrgId(null)
        setOrgPickerIds([])
        setOrgTargetConfig([])
        setExplicitUserIds([])
        const d = new Date()
        d.setMinutes(0, 0, 0)
        const e = new Date(d.getTime() + 60 * 60 * 1000)
        setStartLocal(toLocalInput(d))
        setEndLocal(toLocalInput(e))
        setAllDay(false)
        setRemindersEnabled(true)
        setRem10(false)
        setRem60(true)
        setRem1440(false)
    }

    function closeModal() {
        setModal(null)
        setEditDetail(null)
    }

    function prefillFromDetail(d) {
        setTitle(d.title || '')
        setDescription(d.description || '')
        const s = parseIso(d.startAt)
        const e = parseIso(d.endAt)
        if (s) setStartLocal(toLocalInput(s))
        if (e) setEndLocal(toLocalInput(e))
        setAllDay(!!d.allDay)
        let arr = []
        try {
            if (d.reminderOffsetsJson) arr = JSON.parse(d.reminderOffsetsJson)
        } catch { arr = [] }
        if (!Array.isArray(arr)) arr = []
        setRemindersEnabled(d.useDefaultReminders !== false || arr.length > 0)
        setRem10(arr.includes(10))
        setRem60(arr.includes(60) || (d.useDefaultReminders !== false && !arr.length))
        setRem1440(arr.includes(1440))
        if (d.eventKind === 'ORG' && Array.isArray(d.orgTargets)) {
            const cfg = d.orgTargets.map((t) => ({
                orgId: Number(t.orgId),
                includeDescendants: !!t.includeDescendants,
            }))
            setOrgTargetConfig(cfg)
            setOrgPickerIds(cfg.map((x) => x.orgId))
        } else {
            setOrgTargetConfig([])
            setOrgPickerIds([])
        }
        setExplicitUserIds(Array.isArray(d.explicitUserIds) ? d.explicitUserIds.map(Number) : [])
        setRrule(typeof d.rrule === 'string' ? d.rrule : '')
        setPersonalCategory(d.personalCategory != null && String(d.personalCategory).trim() ? String(d.personalCategory).trim() : '')
        setBusyShareOrgId(d.busyShareOrgId != null ? Number(d.busyShareOrgId) : null)
    }

    function startEditFromDetail(d) {
        if (!d) return
        prefillFromDetail(d)
        setEditDetail(d)
        setModal(d.eventKind === 'ORG' ? 'edit-org' : 'edit-personal')
    }

    const createPersonalMut = useMutation({
        mutationFn: async () => {
            const vr = validateEventStartEndOrder(startLocal, endLocal)
            if (!vr.ok) throw new Error(vr.message)
            const startAt = new Date(startLocal).toISOString()
            const endAt = new Date(endLocal).toISOString()
            const body = {
                title: title.trim(),
                description: description.trim() || null,
                startAt,
                endAt,
                allDay,
                timeZone: 'Asia/Seoul',
                useDefaultReminders: false,
                reminderOffsetsMinutes,
                rrule: allDay ? null : (rrule.trim() || null),
                personalCategory: personalCategory.trim() || null,
                hideDetailsForOrgAdmins: false,
                busyShareOrgId: busyShareOrgId != null && busyShareOrgId > 0 ? busyShareOrgId : null,
            }
            const res = await api.post('/calendar/events/personal', body)
            return unwrap(res)
        },
        onSuccess: () => {
            show('개인 일정이 등록되었습니다', 'success')
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
            closeModal()
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '등록에 실패했습니다'
            show(msg, 'error')
        },
    })

    const createOrgMut = useMutation({
        mutationFn: async () => {
            if (!orgTargetConfig.length) throw new Error('대상 조직을 선택하세요')
            const vr = validateEventStartEndOrder(startLocal, endLocal)
            if (!vr.ok) throw new Error(vr.message)
            const startAt = new Date(startLocal).toISOString()
            const endAt = new Date(endLocal).toISOString()
            const body = {
                hostOrgId: user?.orgId ?? null,
                title: title.trim(),
                description: description.trim() || null,
                startAt,
                endAt,
                allDay,
                timeZone: 'Asia/Seoul',
                useDefaultReminders: false,
                reminderOffsetsMinutes,
                orgTargets: orgTargetConfig.map((r) => ({
                    orgId: r.orgId,
                    includeDescendants: !!r.includeDescendants,
                })),
                explicitUserIds: explicitUserIds.length ? explicitUserIds : null,
                rrule: allDay ? null : (rrule.trim() || null),
            }
            const res = await api.post('/calendar/events/org', body)
            return unwrap(res)
        },
        onSuccess: () => {
            show('조직 일정이 등록되었습니다', 'success')
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
            closeModal()
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '등록에 실패했습니다'
            show(msg, 'error')
        },
    })

    const updateMut = useMutation({
        mutationFn: async ({ id, payload }) => {
            const res = await api.patch(`/calendar/events/${id}`, payload)
            return unwrap(res)
        },
        onSuccess: (_data, variables) => {
            show('일정을 수정했습니다', 'success')
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-event', variables.id] })
            closeModal()
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '수정에 실패했습니다'
            show(msg, 'error')
        },
    })

    function submitEdit() {
        const d = editDetail
        if (!d) return
        const vr = validateEventStartEndOrder(startLocal, endLocal)
        if (!vr.ok) {
            show(vr.message, 'error')
            return
        }
        const startAt = new Date(startLocal).toISOString()
        const endAt = new Date(endLocal).toISOString()
        const base = {
            title: title.trim(),
            description: description.trim() || null,
            startAt,
            endAt,
            allDay,
            timeZone: d.timeZone || 'Asia/Seoul',
            useDefaultReminders: false,
            reminderOffsetsMinutes,
            rrule: allDay ? '' : rrule.trim(),
        }
        if (d.eventKind === 'ORG') {
            updateMut.mutate({
                id: d.id,
                payload: {
                    ...base,
                    orgTargets: orgTargetConfig.map((r) => ({
                        orgId: r.orgId,
                        includeDescendants: !!r.includeDescendants,
                    })),
                    explicitUserIds: [...explicitUserIds],
                },
            })
        } else {
            const hadBusy = d.busyShareOrgId != null && Number(d.busyShareOrgId) > 0
            const clearingBusy = hadBusy && (busyShareOrgId == null || busyShareOrgId <= 0)
            updateMut.mutate({
                id: d.id,
                payload: {
                    ...base,
                    orgTargets: null,
                    explicitUserIds: null,
                    personalCategory: personalCategory.trim() || '',
                    hideDetailsForOrgAdmins: false,
                    busyShareOrgId: busyShareOrgId != null && busyShareOrgId > 0 ? busyShareOrgId : null,
                    ...(clearingBusy ? { clearBusyShareOrg: true } : {}),
                },
            })
        }
    }

    const apiRef = useRef({
        openPersonalAt,
        openOrg,
        startEditFromDetail,
        closeModal,
    })
    apiRef.current = { openPersonalAt, openOrg, startEditFromDetail, closeModal }

    useImperativeHandle(ref, () => ({
        openPersonalAt: (start, end) => apiRef.current.openPersonalAt(start, end),
        openOrg: () => apiRef.current.openOrg(),
        startEditFromDetail: (d) => apiRef.current.startEditFromDetail(d),
        /** 목록 요약만 있을 때 상세 조회 후 수정 모달 */
        openEditFromEventSummary: async (ev) => {
            if (!ev?.id) return
            try {
                const res = await api.get(`/calendar/events/${ev.id}`)
                const d = unwrap(res)
                apiRef.current.startEditFromDetail(d)
            } catch (e) {
                const msg = e?.response?.data?.message || e?.message || '일정을 불러오지 못했습니다'
                show(msg, 'error')
            }
        },
        closeModal: () => apiRef.current.closeModal(),
    }), [show])

    if (!modal) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog">
            <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-4 space-y-3 max-h-[90vh] overflow-y-auto">
                <div className="font-semibold">
                    {modal === 'org' && '조직 일정'}
                    {modal === 'personal' && '개인 일정'}
                    {modal === 'edit-org' && '조직 일정 수정'}
                    {modal === 'edit-personal' && '개인 일정 수정'}
                </div>
                <label className="block text-sm">
                    <span className="text-slate-600">제목</span>
                    <input className="mt-1 w-full rounded border px-3 py-2" value={title} onChange={(e) => setTitle(e.target.value)} />
                </label>
                <div className="flex flex-wrap items-end gap-3">
                    <label className="flex shrink-0 items-center gap-2 pb-2 text-sm">
                        <input
                            type="checkbox"
                            checked={allDay}
                            onChange={(e) => {
                                setAllDay(e.target.checked)
                                if (e.target.checked) setRrule('')
                            }}
                        />
                        종일
                    </label>
                    <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
                        <label className="text-sm">
                            <span className="text-slate-600">시작</span>
                            <input type="datetime-local" className="mt-1 w-full rounded border px-2 py-1.5 text-sm" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
                        </label>
                        <label className="text-sm">
                            <span className="text-slate-600">종료</span>
                            <input type="datetime-local" className="mt-1 w-full rounded border px-2 py-1.5 text-sm" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} />
                        </label>
                    </div>
                </div>
                {!startEndOrder.ok && (
                    <p className="text-xs text-rose-600" role="alert">
                        {startEndOrder.message}
                    </p>
                )}
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 text-sm">
                    <label className="block">
                        <span className="text-slate-600">반복 설정</span>
                        <select
                            className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white disabled:bg-slate-50"
                            disabled={allDay}
                            value={rrule}
                            onChange={(e) => setRrule(e.target.value)}
                        >
                            {RRULE_PRESETS.map((p) => (
                                <option key={p.key} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                        {allDay ? (
                            <p className="mt-0.5 text-[11px] text-slate-500">종일 일정은 반복 없음</p>
                        ) : null}
                    </label>
                    {(modal === 'personal' || modal === 'edit-personal') ? (
                        <label className="block">
                            <span className="text-slate-600">카테고리</span>
                            <select
                                className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
                                value={personalCategory}
                                onChange={(e) => setPersonalCategory(e.target.value)}
                            >
                                {PERSONAL_CATEGORY_OPTIONS.map((o) => (
                                    <option key={o.value || 'none'} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </label>
                    ) : (
                        <div className="flex min-h-[2.75rem] flex-col justify-end rounded border border-slate-100 bg-slate-50/80 px-2 py-1.5 text-xs text-slate-500">
                            조직 일정은 아래에서 대상 조직·인원을 지정합니다.
                        </div>
                    )}
                    {(modal === 'personal' || modal === 'edit-personal') && user?.orgId && String(user?.orgType || '').toUpperCase() !== 'PERSONAL' ? (
                        <label className="flex flex-col gap-1">
                            <span className="text-slate-600">조직 공유</span>
                            <span className="flex items-start gap-2 rounded border border-slate-200 bg-white px-2 py-2">
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    checked={Number(busyShareOrgId) === Number(user.orgId)}
                                    onChange={(e) => {
                                        if (e.target.checked) setBusyShareOrgId(Number(user.orgId))
                                        else setBusyShareOrgId(null)
                                    }}
                                />
                                <span className="text-xs leading-snug text-slate-700">
                                    소속 조직 및 상위 조직의 일정 관리자에게 제목·메모를 포함해 공유합니다.
                                </span>
                            </span>
                        </label>
                    ) : (modal === 'org' || modal === 'edit-org') ? (
                        <div className="hidden sm:block" aria-hidden />
                    ) : null}
                </div>
                {(modal === 'org' || modal === 'edit-org') && (
                    <div className="space-y-2">
                        <div className="text-sm text-slate-600">대상 조직 (조직별로 하위 포함 여부를 지정)</div>
                        <OrgMultiSelect
                            value={orgPickerIds}
                            onChange={syncOrgTargetsFromPicker}
                            scope="mine"
                        />
                        <div className="space-y-2">
                            {orgTargetConfig.map((row) => (
                                <div key={row.orgId} className="flex items-center justify-between gap-2 text-xs border rounded px-2 py-1">
                                    <span className="text-slate-700">{orgNameById.get(row.orgId) || `조직 #${row.orgId}`}</span>
                                    <label className="flex items-center gap-1 shrink-0">
                                        <input
                                            type="checkbox"
                                            checked={!!row.includeDescendants}
                                            onChange={(e) => {
                                                const v = e.target.checked
                                                setOrgTargetConfig((prev) => prev.map((x) => (x.orgId === row.orgId ? { ...x, includeDescendants: v } : x)))
                                            }}
                                        />
                                        하위 포함
                                    </label>
                                </div>
                            ))}
                        </div>
                        <div className="text-sm text-slate-600">지정 인원 (비우면 대상 조직 전원)</div>
                        <div className="max-h-36 overflow-y-auto border rounded p-2 text-xs space-y-1">
                            {candidates.length === 0 && <div className="text-slate-400">대상 조직을 먼저 선택하세요.</div>}
                            {candidates.map((u) => (
                                <label key={u.id} className="flex gap-2 items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={explicitUserIds.includes(Number(u.id))}
                                        onChange={(e) => {
                                            const id = Number(u.id)
                                            setExplicitUserIds((prev) => {
                                                if (e.target.checked) return [...new Set([...prev, id])]
                                                return prev.filter((x) => x !== id)
                                            })
                                        }}
                                    />
                                    <span>{u.name}</span>
                                    <span className="text-slate-400 truncate">{u.email}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
                <fieldset className="rounded border border-slate-200 p-3">
                    <legend className="px-1 text-sm font-medium text-slate-700">알림</legend>
                    <div className="grid grid-cols-2 overflow-hidden rounded border border-slate-200 text-sm">
                        <button
                            type="button"
                            className={`px-3 py-2 font-medium ${remindersEnabled ? 'bg-sky-600 text-white' : 'bg-white text-slate-700'}`}
                            aria-pressed={remindersEnabled}
                            onClick={() => {
                                setRemindersEnabled(true)
                                if (!rem10 && !rem60 && !rem1440) setRem60(true)
                            }}
                        >
                            알림
                        </button>
                        <button
                            type="button"
                            className={`border-l border-slate-200 px-3 py-2 font-medium ${remindersEnabled ? 'bg-white text-slate-700' : 'bg-slate-700 text-white'}`}
                            aria-pressed={!remindersEnabled}
                            onClick={() => {
                                setRemindersEnabled(false)
                                setRem10(false)
                                setRem60(false)
                                setRem1440(false)
                            }}
                        >
                            알림 없음
                        </button>
                    </div>
                    {remindersEnabled && (
                        <div className="mt-3 grid gap-2 text-sm text-slate-700 sm:grid-cols-3">
                            <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                                <input
                                    type="checkbox"
                                    checked={rem10}
                                    onChange={(e) => {
                                        const checked = e.target.checked
                                        setRem10(checked)
                                        if (!checked && !rem60 && !rem1440) setRemindersEnabled(false)
                                    }}
                                />
                                <span>10분 전</span>
                            </label>
                            <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                                <input
                                    type="checkbox"
                                    checked={rem60}
                                    onChange={(e) => {
                                        const checked = e.target.checked
                                        setRem60(checked)
                                        if (!checked && !rem10 && !rem1440) setRemindersEnabled(false)
                                    }}
                                />
                                <span>1시간 전</span>
                            </label>
                            <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                                <input
                                    type="checkbox"
                                    checked={rem1440}
                                    onChange={(e) => {
                                        const checked = e.target.checked
                                        setRem1440(checked)
                                        if (!checked && !rem10 && !rem60) setRemindersEnabled(false)
                                    }}
                                />
                                <span>1일 전</span>
                            </label>
                        </div>
                    )}
                </fieldset>
                <label className="block text-sm">
                    <span className="text-slate-600">설명·메모 (선택)</span>
                    <textarea className="mt-1 w-full rounded border px-3 py-2" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="참석자·장소·준비물 등" />
                </label>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="rounded border px-3 py-1.5 text-sm" onClick={closeModal}>
                        닫기
                    </button>
                    {modal === 'personal' && (
                        <button
                            type="button"
                            className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
                            disabled={!title.trim() || !startEndOrder.ok || createPersonalMut.isPending}
                            onClick={() => createPersonalMut.mutate()}
                        >
                            저장
                        </button>
                    )}
                    {modal === 'org' && (
                        <button
                            type="button"
                            className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
                            disabled={!title.trim() || !startEndOrder.ok || createOrgMut.isPending || !orgTargetConfig.length}
                            onClick={() => createOrgMut.mutate()}
                        >
                            저장
                        </button>
                    )}
                    {(modal === 'edit-personal' || modal === 'edit-org') && (
                        <button
                            type="button"
                            className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
                            disabled={!title.trim() || !startEndOrder.ok || updateMut.isPending}
                            onClick={submitEdit}
                        >
                            수정 저장
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
})

export default CalendarEventEditor
